import React, { useCallback } from 'react';
import { Box, Card, Divider, Typography, Checkbox, useTheme, Skeleton } from '@mui/material';
import useTouchInteraction from '../../../hooks/useTouchInteraction';
import { formatPriceWithPrecision } from '../../../utils/utilfunction';
import ExchangeTag from '../../Common/ExchangeTag';

const PositionCard = React.memo(({ 
  position,
  selectedPosition,
  handleCheckbox,
  formatProduct,
  exchangeSpecificStringifiedNumber,
  onRowClick
}) => {
  const theme = useTheme();

  const formatPositionPrice = (positionData, price) => {
    if (positionData.exchange === 'CDS' && positionData.price_precision !== undefined) {
      return formatPriceWithPrecision(price, positionData.price_precision, {
        showCurrency: false,
        showCommas: false,
        removeTrailingZeros: false
      });
    }
    return exchangeSpecificStringifiedNumber(positionData.exchange, price);
  };

  const isSelected = selectedPosition.some(
    item => item.token === position.token &&
      item.exchange === position.exchange &&
      item.product === position.product
  );

  const handleTap = useCallback((e) => {
    if (e.target.closest('.checkbox-container')) {
      return;
    }
    // Tap anywhere else on the card opens the details drawer
    onRowClick(position);
  }, [position, onRowClick]);

  const { handlers, touchStates } = useTouchInteraction(handleTap, true);

  const qty = position.net_quantity;
  const qtyColor = qty > 0 ? theme.palette.success.main : qty < 0 ? theme.palette.error.main : theme.palette.text.primary;
  const pnlColor = position.pnl >= 0 ? theme.palette.success.main : theme.palette.error.main;
  const sideLabel = qty > 0 ? 'LONG' : qty < 0 ? 'SHORT' : '';

  // Metric label/value pairs laid out in a wrapping flex row (Orders-card style)
  // so values keep their natural width and never truncate.
  const metrics = [
    { label: 'Qty', value: qty, color: qtyColor },
    ...(qty < 0 ? [] : [{ label: 'Buy Avg', value: formatPositionPrice(position, position.buyAverage) }]),
    ...(qty > 0 ? [] : [{ label: 'Sell Avg', value: formatPositionPrice(position, position.sellAverage) }]),
    { label: 'Blocked Margin', value: formatPositionPrice(position, position.blocked_margin) },
    {
      label: 'Price',
      color: qtyColor,
      value: position.isLtpStale
        ? <Skeleton animation="wave" width={44} height={14} />
        : formatPositionPrice(position, position.ltp),
    },
  ];

  return (
    <Card
      elevation={0}
      {...handlers}
      component="div"
      role="button"
      sx={{
        mb: 0.5,
        borderRadius: 1,
        position: 'relative',
        borderLeft: `3px solid ${qty === 0 ? theme.palette.divider : qtyColor}`,
        backgroundColor: isSelected ? theme.palette.action.selected : theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderLeftWidth: 3,
        borderLeftColor: qty === 0 ? theme.palette.divider : qtyColor,
        cursor: qty !== 0 ? 'pointer' : 'default',
        WebkitTapHighlightColor: 'transparent',
        userSelect: 'none',
        touchAction: 'pan-y',
        WebkitUserSelect: 'none',
        '&:active': qty !== 0 ? {
          backgroundColor: (touchStates.isActive && !touchStates.isTouchMoved)
            ? theme.palette.action.selected
            : theme.palette.background.paper,
        } : {},
        transition: 'background-color 0.15s ease',
      }}
    >
      <Box sx={{ px: { xs: 1, sm: 1.5 }, py: { xs: 0.75, sm: 1 } }}>
        {/* Header: symbol + side (left), P&L + checkbox (right) — always visible */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 0.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography noWrap variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8125rem', lineHeight: 1.2, pointerEvents: 'none' }}>
                {position.trading_symbol}
              </Typography>
              {sideLabel && (
                <Typography sx={{ fontSize: '0.625rem', fontWeight: 700, color: qtyColor, pointerEvents: 'none' }}>
                  {sideLabel}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ExchangeTag exchange={position.exchange} />
              <Typography noWrap variant="caption" sx={{ fontSize: '0.65rem', color: theme.palette.text.secondary, lineHeight: 1.2, pointerEvents: 'none' }}>
                • {formatProduct(position.product)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, maxWidth: '55%' }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{ display: 'block', fontSize: '0.6rem', color: theme.palette.text.secondary, lineHeight: 1.2 }}>
                P&L
              </Typography>
              {position.isLtpStale ? (
                <Skeleton animation="wave" width={56} height={16} sx={{ ml: 'auto' }} />
              ) : (
                <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', lineHeight: 1.2, color: pnlColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  ₹{formatPositionPrice(position, Math.abs(position.pnl))}
                  <Box component="span" sx={{ fontSize: '0.6rem', ml: 0.5 }}>
                    ({(Number.isFinite(position.pnl_percent_change) ? position.pnl_percent_change : 0).toFixed(2)}%)
                  </Box>
                </Typography>
              )}
            </Box>
            <Box
              className="checkbox-container"
              sx={{ zIndex: 1, flexShrink: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                if (qty !== 0) {
                  const syntheticEvent = {
                    target: { checked: !isSelected, name: '' },
                    preventDefault: () => {},
                    stopPropagation: () => {},
                  };
                  handleCheckbox(syntheticEvent, position);
                }
              }}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
            >
              <Checkbox
                checked={isSelected}
                disabled={qty === 0}
                size="small"
                sx={{ p: 0, pointerEvents: 'none', '& .MuiSvgIcon-root': { fontSize: '1.1rem' } }}
              />
            </Box>
          </Box>
        </Box>

        {/* Permanently expanded metrics section */}
        <Box className="metrics-container">
          <Divider sx={{ my: { xs: 0.5, sm: 0.75 } }} />

          {/* Metrics: responsive grid — distributes evenly across all widths */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(70px, 1fr))',
            gap: { xs: 0.5, sm: 0.75 },
          }}>
            {metrics.map((m, i) => (
              <Box key={i}>
                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.6rem', color: theme.palette.text.secondary, lineHeight: 1.2 }}>
                  {m.label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.75rem', lineHeight: 1.3, color: m.color || theme.palette.text.primary, whiteSpace: 'nowrap' }}>
                  {m.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Card>
  );
});



const MobileView = ({
  positions,
  selectedPosition,
  handleCheckbox,
  formatProduct,
  exchangeSpecificStringifiedNumber,
  onRowClick,
  getPositionPendingOrders
}) => {
  const theme = useTheme();

  return (
    <Box sx={{
      px: { xs: 0.75, sm: 1 },
      pt: 0.5,
      WebkitTapHighlightColor: 'transparent',
      backgroundColor: theme.palette.background.default,
      pb: 2
    }}>
      {positions?.map((position) => {
        const cardKey = position.token + position.exchange + position.product;
        return (
          <PositionCard
            key={cardKey}
            position={position}
            selectedPosition={selectedPosition}
            handleCheckbox={handleCheckbox}
            formatProduct={formatProduct}
            exchangeSpecificStringifiedNumber={exchangeSpecificStringifiedNumber}
            onRowClick={onRowClick}
          />
        );
      })}
    </Box>
  );
};

export default MobileView;


